import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import useError from 'hooks/useError';
import useOrders from 'hooks/useOrders';
import useRole from 'hooks/useRole';
import useTowlotVehicles from 'hooks/useTowlotVehicles';

/**
 * Debounced multi-source order / auction search for the app bar.
 */
const useGlobalSearch = (query) => {
  const {
    getCustomersOrders,
    getInvoicesOrders,
    getVehiclesOrders,
    clearLocalCustomersOrders,
    clearLocalInvoicesOrders,
    clearLocalVehiclesOrders
  } = useOrders();
  const { getAuctionTowlotVehicles, clearLocalAuctionTowlotVehicles } = useTowlotVehicles();
  const { isOwner } = useRole();
  const { setError } = useError();
  const [fetching, setFetching] = useState(false);

  const fetchCustomersOrders = useCallback(
    (offset, q) => {
      setFetching(true);
      getCustomersOrders({ offset, customer_query: q })
        .catch((error) => !axios.isCancel(error) && setError(error))
        .finally(() => setFetching(false));
    },
    [getCustomersOrders, setError]
  );

  const fetchInvoicesOrders = useCallback(
    (offset, q) => {
      setFetching(true);
      getInvoicesOrders({ offset, self_invoice_query: q })
        .catch((error) => !axios.isCancel(error) && setError(error))
        .finally(() => setFetching(false));
    },
    [getInvoicesOrders, setError]
  );

  const fetchVehiclesOrders = useCallback(
    (offset, q) => {
      setFetching(true);
      getVehiclesOrders({ offset, vehicle_query: q })
        .catch((error) => !axios.isCancel(error) && setError(error))
        .finally(() => setFetching(false));
    },
    [getVehiclesOrders, setError]
  );

  const fetchAuctionTowlot = useCallback(
    (offset, q) => {
      if (!isOwner) return Promise.resolve();
      setFetching(true);
      return getAuctionTowlotVehicles({ offset, query: q })
        .catch((error) => !axios.isCancel(error) && setError(error))
        .finally(() => setFetching(false));
    },
    [getAuctionTowlotVehicles, isOwner, setError]
  );

  useEffect(() => {
    let timeout;
    if (query) {
      timeout = setTimeout(() => {
        setFetching(true);
        const clearAuction = isOwner ? clearLocalAuctionTowlotVehicles() : Promise.resolve();
        Promise.all([
          clearLocalCustomersOrders(),
          clearLocalInvoicesOrders(),
          clearLocalVehiclesOrders(),
          clearAuction
        ])
          .then(() =>
            Promise.all([
              getCustomersOrders({ offset: 0, customer_query: query }).catch((error) => {
                if (!axios.isCancel(error)) setError(error);
              }),
              getInvoicesOrders({ offset: 0, self_invoice_query: query }).catch((error) => {
                if (!axios.isCancel(error)) setError(error);
              }),
              getVehiclesOrders({ offset: 0, vehicle_query: query }).catch((error) => {
                if (!axios.isCancel(error)) setError(error);
              }),
              ...(isOwner
                ? [
                    getAuctionTowlotVehicles({ offset: 0, query }).catch((error) => {
                      if (!axios.isCancel(error)) setError(error);
                    })
                  ]
                : [])
            ])
          )
          .finally(() => setFetching(false));
      }, 500);
    }
    return () => {
      clearTimeout(timeout);
      clearLocalCustomersOrders();
      clearLocalInvoicesOrders();
      clearLocalVehiclesOrders();
      clearLocalAuctionTowlotVehicles();
    };
  }, [
    getCustomersOrders,
    getInvoicesOrders,
    getVehiclesOrders,
    getAuctionTowlotVehicles,
    clearLocalCustomersOrders,
    clearLocalInvoicesOrders,
    clearLocalVehiclesOrders,
    clearLocalAuctionTowlotVehicles,
    isOwner,
    query,
    setError
  ]);

  return {
    fetching,
    fetchCustomersOrders,
    fetchInvoicesOrders,
    fetchVehiclesOrders,
    fetchAuctionTowlot
  };
};

export default useGlobalSearch;
